import { AxiosInstance } from "axios";
import jwtDecode from "jwt-decode";
import * as base64js from "base64-js";
import * as pako from "pako";
import { NO_ACCESS_TOKEN_FOUND_IN_AUTH_RESPONSE } from "./constants";

type CustomerId = string;
type SiteId = string;

type GlobalAccessTokenContext = {
  customerId: null;
  siteId: null;
};

type CustomerAccessTokenContext = {
  customerId: string;
  siteId: null;
};

type SiteAccessTokenContext = {
  customerId: string;
  siteId: string;
};

export type AccessTokenContext =
  | GlobalAccessTokenContext
  | CustomerAccessTokenContext
  | SiteAccessTokenContext;

export type Acl = {
  systemLevel: string[];
  customerLevel: {
    [permissionId: string]: CustomerId[];
  };
  siteLevel: {
    [permissionId: string]: SiteId[];
  };
};

export type UserInfo = {
  email: string;
};

type AccessToken = string;

interface IAccessTokenContents {
  exp: number;
  iat: number;
  payload: string;
}

export interface IAccessTokenManagerOptions {
  loginServiceUrl: string;
  tokenUpdateOvertakeFactor: number;
}

type AccessTokenContextStringRepresentation = string;

type AccessTokenCache = Record<
  AccessTokenContextStringRepresentation,
  Promise<AccessToken>
>;

export default class AccessTokenManager {
  private readonly axios: AxiosInstance;
  private readonly loginServiceUrl: string;
  private accessTokenCache: AccessTokenCache;
  private clientTimeOffsetMs: number;
  private readonly tokenUpdateOvertakeFactor: number;

  constructor(axios: AxiosInstance, options: IAccessTokenManagerOptions) {
    this.axios = axios;
    this.loginServiceUrl = options.loginServiceUrl;
    this.accessTokenCache = {};
    this.clientTimeOffsetMs = 0;
    this.tokenUpdateOvertakeFactor = options.tokenUpdateOvertakeFactor;
    return this;
  }

  public async getToken(tokenContext: AccessTokenContext): Promise<string> {
    const cachedAccessTokenPromise = this.accessTokenCache[
      AccessTokenManager.tokenContextToStringRepresentation(tokenContext)
    ];

    if (!cachedAccessTokenPromise) {
      return this.forceUpdateToken(tokenContext);
    }

    const accessToken = await cachedAccessTokenPromise;

    if (!this.tokenIsExpired(accessToken)) {
      return accessToken;
    }

    return this.forceUpdateToken(tokenContext);
  }

  public async forceUpdateToken(
    tokenContext: AccessTokenContext,
  ): Promise<string> {
    // Requesting fresh token if customer context was changed or token is expired
    const tokenFetchPromise = this.fetchToken(tokenContext);
    this.accessTokenCache[
      AccessTokenManager.tokenContextToStringRepresentation(tokenContext)
    ] = tokenFetchPromise;

    const token = await tokenFetchPromise;

    this.clientTimeOffsetMs = AccessTokenManager.getClientTimeOffset(token);

    return token;
  }

  public async getAcl(tokenContext: AccessTokenContext): Promise<Acl> {
    const token = await this.getToken(tokenContext);
    return AccessTokenManager.extractTokenPayload(token).ACL;
  }

  public async getUserInfo(
    tokenContext: AccessTokenContext,
  ): Promise<UserInfo> {
    const token = await this.getToken(tokenContext);
    return AccessTokenManager.extractTokenPayload(token).user;
  }

  public async logout(): Promise<void> {
    await this.axios({
      method: "POST",
      url: `${this.loginServiceUrl}/api/logout`,
      withCredentials: true,
    });
  }

  private tokenIsExpired(accessToken: string): boolean {
    const { iat, exp }: IAccessTokenContents = jwtDecode(accessToken);
    const iatMs = iat * 1000;
    const expMs = exp * 1000;
    const tokenTtlMs = expMs - iatMs;
    const updateThreshold = iatMs + tokenTtlMs * this.tokenUpdateOvertakeFactor;
    const currentTimestamp = Date.now() - this.clientTimeOffsetMs;
    return updateThreshold - currentTimestamp < 0;
  }

  private static getClientTimeOffset(token: string): number {
    const { iat }: IAccessTokenContents = jwtDecode(token);
    return Date.now() - iat * 1000;
  }

  private async fetchToken(context: AccessTokenContext): Promise<string> {
    const resp = await this.axios({
      method: "POST",
      url: `${this.loginServiceUrl}/api/me`,
      data: this.tokenContextToCsuRequestPayload(context),
      withCredentials: true,
    });
    if (!resp.data || !resp.data.accessToken) {
      throw new Error(NO_ACCESS_TOKEN_FOUND_IN_AUTH_RESPONSE);
    }
    return resp.data.accessToken;
  }

  private tokenContextToCsuRequestPayload = ({
    customerId,
    siteId,
  }: AccessTokenContext) => {
    if (!customerId) {
      return null;
    }

    if (!siteId) {
      return {
        customer: customerId,
      };
    }

    return {
      customer: customerId,
      site: siteId,
    };
  };

  private static extractTokenPayload(
    accessToken: string,
  ): { ACL: Acl; user: UserInfo } {
    const { payload }: IAccessTokenContents = jwtDecode(accessToken);
    const deflatedPayload = base64js.toByteArray(payload);
    return JSON.parse(pako.inflate(deflatedPayload, { to: "string" }));
  }

  private static tokenContextToStringRepresentation({
    customerId,
    siteId,
  }: AccessTokenContext): AccessTokenContextStringRepresentation {
    return `${customerId}/${siteId}`;
  }
}
